// Team Workload (Technical Department) - the values the page, its components and its helpers share.

/* ── API ────────────────────────────────────────────────────────────────── */

/** Everything the page draws, for a date span: GET ?from=YYYY-MM-DD&to=YYYY-MM-DD. */
export const OVERVIEW_URL = 'tech-workload/overview';
/** Planned hours: PUT `{ taskId, personId, plannedHours }`, DELETE `/:hourId`. */
export const HOURS_URL = 'tech-workload/hours';
/** Logged time: POST `{ hourId, workDate, fromTime, toTime, note }`, PATCH / DELETE `/:entryId`. */
export const ENTRIES_URL = 'tech-workload/entries';

/* ── InteTasks task statuses ────────────────────────────────────────────── */

export const STATUS = {
  PENDING: 1,
  IN_PROGRESS: 2,
  ON_HOLD: 3,
  REVIEW: 4,
  COMPLETED: 5,
  APPROVED: 6,
};

export const STATUS_LABEL = {
  1: 'Open',
  2: 'In progress',
  3: 'On hold',
  4: 'Review pending',
  5: 'Completed',
  6: 'Approved',
};

/** In review, approved or completed: the work has left the assignee's hands. */
export const HANDED_OVER = [STATUS.REVIEW, STATUS.APPROVED, STATUS.COMPLETED];

/** The status pill's colours, by status number. */
export const STATUS_TONE = {
  1: { bg: '#EFEDE6', text: '#5C5C57' }, // Open
  2: { bg: '#E8F1FB', text: '#1D5A99' }, // In progress
  3: { bg: '#FEF6E7', text: '#93590A' }, // On hold
  4: { bg: '#EDE7F8', text: '#4A3190' }, // Review pending
  5: { bg: '#E3F3EA', text: '#146C43' }, // Completed
  6: { bg: '#E0F2F1', text: '#0F5E57' }, // Approved
};

/* ── Load levels ────────────────────────────────────────────────────────── */

export const LOAD_LEVELS = {
  overloaded: { key: 'overloaded', label: 'Overloaded', rule: 'More than 100% of capacity' },
  atCapacity: { key: 'atCapacity', label: 'At capacity', rule: '85 – 100%' },
  balanced: { key: 'balanced', label: 'Balanced', rule: '50 – 84%' },
  available: { key: 'available', label: 'Available', rule: 'Under 50%' },
};

export const LOAD_ORDER = ['overloaded', 'atCapacity', 'balanced', 'available'];

/** One colour family per load level: the bar/dot, the filled background, the text. */
export const LEVEL_TONE = {
  overloaded: { accent: '#D92D20', bg: '#FEF3F2', text: '#B42318' },
  atCapacity: { accent: '#DC8B0B', bg: '#FEF6E7', text: '#93590A' },
  balanced: { accent: '#1F8A5B', bg: '#E3F3EA', text: '#146C43' },
  available: { accent: '#2E78C7', bg: '#E8F1FB', text: '#1D5A99' },
};

/** A task's "due …" text in the drawer: red when late, amber when close, grey otherwise. */
export const DUE_COLOR = {
  overdue: LEVEL_TONE.overloaded.text,
  soon: LEVEL_TONE.atCapacity.text,
  ok: '#9A9A93',
  none: '#9A9A93',
};

/** The load bar's track is drawn to 150%, with a tick at 100%. */
export const BAR_SCALE = 150;

/* ── Look ───────────────────────────────────────────────────────────────── */

/** Background of every card - a light grey, so the cards stand out on the portal's white page. */
export const CARD_BG = '#F8F9FB';
/** The page and the person drawer: the portal's own white. */
export const PAGE_BG = '#FFFFFF';
/** Days off and days outside the month, in the calendar. */
export const OFF_DAY_BG = '#EEF0F3';
/** Row / item hover inside a card. */
export const HOVER_BG = '#FAF8F3';
/** The expanded hours area under a task in the drawer. */
export const SUNKEN_BG = '#F7F5F0';

/** Figures line up in columns. */
export const FIGURE = { fontVariantNumeric: 'tabular-nums' };

/* ── Filters ────────────────────────────────────────────────────────────── */

export const VIEWS = [
  { key: 'list', label: 'List' },
  { key: 'calendar', label: 'Calendar' },
];

export const PRESETS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
